/**
 * DokumentePage — Dokumentenbereich mit Explorer-Ansicht + Vorschau-Panel
 * 
 * Features:
 * - Ordner-Navigation (Baumstruktur, 154+ Ordner)
 * - Datei-Upload (Drag & Drop + Button)
 * - Inline Vorschau (PDF, Bilder, Text)
 * - Metadaten & Verknüpfungen (Kontakte, Fälle, Aktenzeichen)
 * - Buchungsfunktion (Status, Betrag, Freigabe)
 * - Dokumenttyp-Erkennung (automatisch + manuell)
 * - Filter-System (Typ, Status, Favoriten, Datum)
 * - Drag & Drop zum Verschieben von Dateien zwischen Ordnern
 * - Ordner-Übersicht mit Pflichtdokument-Status für Firmen/Immobilien
 * - Suche über alle Dokumente
 * - Privater Bereich mit PIN
 * - Mobile-optimiert
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/useMobile';
import { useMobileBackHandler, useMobileDetailBackHandler } from '@/hooks/useMobileBackHandler';
import {
  useOrdner,
  useDokumente,
  useUpload,
  useDokumentAktionen,
  useDokumentSuche,
  useVerknuepfungen,
  usePflichtdokumente,
  useTags,
  BUCHUNGS_STATUS,
  FREIGABE_STATUS,
  DOKUMENT_TYPEN,
  VERKNUEPFUNG_TYPEN,
  canPreviewInline,
  getPreviewType,
  type DokumentOrdner,
  type DokumentDatei,
  type DokumentTag,
  type PflichtDokument,
  type SuchErgebnis,
  type OrdnerSuchErgebnis,
} from '@/hooks/useDokumente';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  FolderOpen, FileText, Download, Trash2, Search, Upload, Lock, ChevronRight,
  Home, Plus, Star, File, Image, FileSpreadsheet,
  FileArchive, Film, Music, X, Check, FolderPlus, ArrowLeft,
  Inbox, Building2, Calculator, Users, Shield, FileSignature, Eye,
  ChevronDown, Loader2, AlertCircle, CloudUpload, MoreHorizontal,
  Link2, Tag, Receipt, Pencil, Save, ExternalLink, ChevronLeft,
  CheckCircle2, Clock, XCircle, CircleDot, FolderInput, StickyNote,
  AlertTriangle, CircleCheck, Circle, UploadCloud, Award, Globe,
  Filter, SlidersHorizontal, CalendarDays, GripVertical, Briefcase,
  BarChart3, FileCheck, FolderTree, ListFilter, LayoutGrid, Edit3,
  ArrowRight, MapPin, Sparkles, Hash, FolderSearch,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  StellungnahmeBadge,
  hasStellungnahmeLabel,
} from '@/components/akte/StellungnahmeLabel';

// ============================================================
// ICON MAPPING
// ============================================================

const ORDNER_ICONS: Record<string, any> = {
  inbox: Inbox, building: Building2, 'building-2': Building2, 'file-text': FileText, receipt: Receipt,
  'file-template': FileText, mail: FileText, clipboard: FileText, truck: FileText,
  'file-signature': FileSignature, car: FileText, 'folder-open': FolderOpen,
  calculator: Calculator, 'arrow-down': Download, 'arrow-up': Upload,
  database: FileSpreadsheet, 'bar-chart': FileSpreadsheet, users: Users,
  banknote: FileText, 'user-plus': Users, shield: Shield, 'file-check': FileText,
  'alert-triangle': AlertCircle, home: Home, handshake: FileText, lock: Lock,
  user: Users, wallet: Receipt, folder: FolderOpen, award: Award, globe: Globe,
  briefcase: Briefcase,
};

function getOrdnerIcon(iconName: string | null) {
  if (!iconName) return FolderOpen;
  return ORDNER_ICONS[iconName] || FolderOpen;
}

function getFileIcon(mimeType: string | null) {
  if (!mimeType) return File;
  if (mimeType.startsWith('image/')) return Image;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) return FileSpreadsheet;
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('tar')) return FileArchive;
  if (mimeType.startsWith('video/')) return Film;
  if (mimeType.startsWith('audio/')) return Music;
  if (mimeType.includes('pdf')) return FileText;
  return File;
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatCurrency(amount: number | null, currency: string = 'EUR'): string {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount);
}

// ============================================================
// FILTER TYPES
// ============================================================

interface FilterState {
  typ: string | null;
  status: string | null;
  favoriten: boolean;
  zeitraum: string | null; // 'heute', 'woche', 'monat', 'quartal', 'jahr'
}

const ZEITRAUM_OPTIONEN = [
  { value: 'heute', label: 'Heute' },
  { value: 'woche', label: 'Diese Woche' },
  { value: 'monat', label: 'Dieser Monat' },
  { value: 'quartal', label: 'Dieses Quartal' },
  { value: 'jahr', label: 'Dieses Jahr' },
];

const DEFAULT_FILTER: FilterState = { typ: null, status: null, favoriten: false, zeitraum: null };

function isFilterActive(filter: FilterState): boolean {
  return filter.typ !== null || filter.status !== null || filter.favoriten || filter.zeitraum !== null;
}

function filterDokumente(docs: DokumentDatei[], filter: FilterState): DokumentDatei[] {
  let result = docs;
  if (filter.typ) result = result.filter(d => d.dokument_typ === filter.typ);
  if (filter.status) result = result.filter(d => d.buchungs_status === filter.status);
  if (filter.favoriten) result = result.filter(d => d.ist_favorit);
  if (filter.zeitraum) {
    const now = new Date();
    let start: Date;
    switch (filter.zeitraum) {
      case 'heute': start = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
      case 'woche': { const d = new Date(now); d.setDate(d.getDate() - d.getDay() + 1); d.setHours(0,0,0,0); start = d; break; }
      case 'monat': start = new Date(now.getFullYear(), now.getMonth(), 1); break;
      case 'quartal': start = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1); break;
      case 'jahr': start = new Date(now.getFullYear(), 0, 1); break;
      default: start = new Date(0);
    }
    result = result.filter(d => new Date(d.erstellt_am) >= start);
  }
  return result;
}

// ============================================================
// ORDNER-BAUM KOMPONENTE (mit Drag-Target Highlighting)
// ============================================================

function OrdnerTree({
  ordner, selectedId, onSelect, level = 0, expandedIds, onToggleExpand,
  dragOverId, onDragOver, onDragLeave, onDrop,
  onRename, onDelete,
}: {
  ordner: DokumentOrdner[]; selectedId: string | null;
  onSelect: (id: string, ordner: DokumentOrdner) => void; level?: number;
  expandedIds: Set<string>; onToggleExpand: (id: string) => void;
  dragOverId?: string | null;
  onDragOver?: (e: React.DragEvent, id: string) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, targetOrdnerId: string) => void;
  onRename?: (id: string, currentName: string) => void;
  onDelete?: (id: string, name: string) => void;
}) {
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; ordnerId: string; ordnerName: string } | null>(null);

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [contextMenu]);

  return (
    <div>
      {ordner.map((o) => {
        const Icon = getOrdnerIcon(o.icon);
        const hasChildren = o.children && o.children.length > 0;
        const isExpanded = expandedIds.has(o.id);
        const isSelected = selectedId === o.id;
        const isDragTarget = dragOverId === o.id;
        return (
          <div key={o.id}>
            <button
              onClick={() => { onSelect(o.id, o); if (hasChildren) onToggleExpand(o.id); }}
              onContextMenu={(e) => {
                if (o.ist_system) return; // System-Ordner nicht bearbeitbar
                e.preventDefault();
                setContextMenu({ x: e.clientX, y: e.clientY, ordnerId: o.id, ordnerName: o.name });
              }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver?.(e, o.id); }}
              onDragLeave={(e) => { e.preventDefault(); onDragLeave?.(e); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop?.(e, o.id); }}
              className={`w-full flex items-center gap-2 px-2 py-1.5 min-h-[34px] rounded-lg text-sm transition-all ${
                isDragTarget
                  ? 'bg-teal-100 text-teal-800 border-2 border-teal-400 border-dashed scale-[1.02]'
                  : isSelected
                  ? 'bg-teal-50 text-teal-800 font-medium border border-teal-200/60'
                  : 'text-gray-600 hover:text-foreground hover:bg-gray-50'
              }`}
              style={{ paddingLeft: `${8 + level * 16}px` }}
            >
              {hasChildren ? (
                <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
              ) : (
                <span className="w-3.5 flex-shrink-0" />
              )}
              <Icon className={`w-4 h-4 flex-shrink-0 ${isDragTarget ? 'text-teal-600' : isSelected ? 'text-teal-600' : 'text-gray-400'}`} />
              <span className="truncate flex-1 text-left">{o.name}</span>
              {o.ist_privat && <Lock className="w-3 h-3 text-gray-400 flex-shrink-0" />}
            </button>
            {hasChildren && isExpanded && (
              <OrdnerTree ordner={o.children!} selectedId={selectedId} onSelect={onSelect}
                level={level + 1} expandedIds={expandedIds} onToggleExpand={onToggleExpand}
                dragOverId={dragOverId} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
                onRename={onRename} onDelete={onDelete} />
            )}
          </div>
        );
      })}
      {/* Kontextmenü */}
      {contextMenu && (
        <div
          className="fixed bg-white border rounded-lg shadow-xl py-1 z-[100] min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => { onRename?.(contextMenu.ordnerId, contextMenu.ordnerName); setContextMenu(null); }}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <Edit3 className="w-3.5 h-3.5" /> Umbenennen
          </button>
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button
              onClick={() => { onDelete?.(contextMenu.ordnerId, contextMenu.ordnerName); setContextMenu(null); }}
              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <div>
                <span>Ordner löschen</span>
                <p className="text-[9px] text-red-400 font-normal">Erfordert Bestätigung</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// UPLOAD ZONE
// ============================================================

function UploadZone({ ordnerId, onUploadComplete }: { ordnerId: string | null; onUploadComplete: () => void; }) {
  const { user } = useAuth();
  const { uploads, uploadMultiple, clearUploads } = useUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    toast(`${fileArray.length} Datei(en) werden hochgeladen...`);
    const results = await uploadMultiple(fileArray, ordnerId, user?.id);
    if (results.length > 0) { toast.success(`${results.length} Datei(en) erfolgreich hochgeladen`); onUploadComplete(); }
    if (results.length < fileArray.length) toast.error(`${fileArray.length - results.length} Datei(en) fehlgeschlagen`);
    setTimeout(clearUploads, 3000);
  }, [ordnerId, user?.id, uploadMultiple, clearUploads, onUploadComplete]);

  return (
    <>
      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files); }}
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
          isDragging ? 'border-teal-400 bg-teal-50/50 scale-[1.01]' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
        }`}
        onClick={() => fileInputRef.current?.click()}
      >
        <CloudUpload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-teal-500' : 'text-gray-300'}`} />
        <p className="text-sm text-gray-500">{isDragging ? 'Dateien hier ablegen' : 'Dateien hierher ziehen oder klicken'}</p>
        <p className="text-[11px] text-gray-400 mt-1">Max. 50 MB pro Datei</p>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => e.target.files && handleFiles(e.target.files)} />
      </div>
      {uploads.length > 0 && (
        <div className="mt-3 space-y-2">
          {uploads.map((u, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{u.fileName}</p>
                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                  <div className={`h-1.5 rounded-full transition-all ${u.status === 'error' ? 'bg-red-400' : u.status === 'done' ? 'bg-green-400' : 'bg-teal-400'}`} style={{ width: `${u.progress}%` }} />
                </div>
              </div>
              {u.status === 'done' && <Check className="w-4 h-4 text-green-500 flex-shrink-0" />}
              {u.status === 'error' && <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
              {u.status === 'uploading' && <Loader2 className="w-4 h-4 text-teal-500 animate-spin flex-shrink-0" />}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ============================================================
// NEUER ORDNER DIALOG
// ============================================================

function NeuerOrdnerDialog({ parentId, onClose, onCreated }: { parentId: string | null; onClose: () => void; onCreated: () => void; }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const { createOrdner } = useOrdner();

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await createOrdner(name.trim(), parentId);
      toast.success(`Ordner "${name}" erstellt`);
      onCreated(); onClose();
    } catch (e: any) {
      toast.error('Fehler: ' + (e.message || 'Ordner konnte nicht erstellt werden'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl p-4 sm:p-6 w-full max-w-sm max-h-[85vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <FolderPlus className="w-5 h-5 text-teal-600" />
          <h3 className="text-lg font-bold">Neuer Ordner</h3>
        </div>
        <input type="text" placeholder="Ordnername eingeben..." value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 text-sm" autoFocus />
        <div className="flex gap-2 mobile-dialog-actions sm:pb-0">
          <button onClick={onClose} className="flex-1 px-3 py-2.5 min-h-[44px] rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50">Abbrechen</button>
          <button onClick={handleCreate} disabled={!name.trim() || saving}
            className="flex-1 px-3 py-2.5 min-h-[44px] rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PIN DIALOG
// ============================================================

function PinDialog({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const handleSubmit = async () => {
    try {
      const { data } = await supabase.from('app_settings').select('value').eq('key', 'kiosk_pin').single();
      if (pin === (data?.value || '123456')) { toast.success('Privater Bereich entsperrt'); onSuccess(); }
      else { setError('PIN falsch'); setPin(''); }
    } catch {
      if (pin === '123456') onSuccess();
      else { setError('PIN falsch'); setPin(''); }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl p-4 sm:p-6 w-full max-w-sm">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="w-5 h-5 text-teal-600" />
          <h3 className="text-lg font-bold">Privater Bereich</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">Geben Sie Ihre PIN ein.</p>
        <input type="password" placeholder="6-stellige PIN" value={pin}
          onChange={(e) => { setPin(e.target.value); setError(''); }}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-teal-500/20 text-sm tracking-widest text-center"
          maxLength={6} autoFocus />
        {error && <p className="text-xs text-red-500 mb-4 text-center">{error}</p>}
        <div className="flex gap-2 mobile-dialog-actions sm:pb-0">
          <button onClick={onClose} className="flex-1 px-3 py-2.5 min-h-[44px] rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50">Abbrechen</button>
          <button onClick={handleSubmit} className="flex-1 px-3 py-2.5 min-h-[44px] rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700">Entsperren</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// FILTER PANEL
// ============================================================

function FilterPanel({
  filter, onChange, onReset, dokumentCount, filteredCount,
}: {
  filter: FilterState; onChange: (f: FilterState) => void; onReset: () => void;
  dokumentCount: number; filteredCount: number;
}) {
  return (
    <div className="px-4 py-3 bg-gray-50/80 border-b border-gray-100 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-3.5 h-3.5 text-gray-500" />
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filter</span>
        </div>
        {isFilterActive(filter) && (
          <button onClick={onReset} className="text-[11px] text-teal-600 font-medium hover:text-teal-700">
            Zurücksetzen
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {/* Dokumenttyp */}
        <select
          value={filter.typ || ''}
          onChange={(e) => onChange({ ...filter, typ: e.target.value || null })}
          className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 min-w-[120px]"
        >
          <option value="">Alle Typen</option>
          {DOKUMENT_TYPEN.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        {/* Buchungsstatus */}
        <select
          value={filter.status || ''}
          onChange={(e) => onChange({ ...filter, status: e.target.value || null })}
          className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 min-w-[120px]"
        >
          <option value="">Alle Status</option>
          {BUCHUNGS_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        {/* Zeitraum */}
        <select
          value={filter.zeitraum || ''}
          onChange={(e) => onChange({ ...filter, zeitraum: e.target.value || null })}
          className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 min-w-[120px]"
        >
          <option value="">Alle Zeiträume</option>
          {ZEITRAUM_OPTIONEN.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
        </select>

        {/* Favoriten */}
        <button
          onClick={() => onChange({ ...filter, favoriten: !filter.favoriten })}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
            filter.favoriten
              ? 'bg-amber-50 border-amber-300 text-amber-700'
              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Star className={`w-3 h-3 ${filter.favoriten ? 'fill-amber-400 text-amber-400' : ''}`} />
          Favoriten
        </button>
      </div>

      {isFilterActive(filter) && (
        <p className="text-[11px] text-gray-400">
          {filteredCount} von {dokumentCount} Dokumenten
        </p>
      )}
    </div>
  );
}

// ============================================================
// ORDNER-ÜBERSICHT (Firmen / Immobilien / Marken)
// ============================================================

function OrdnerUebersicht({
  ordner, childOrdner, onNavigate,
}: {
  ordner: DokumentOrdner;
  childOrdner: DokumentOrdner[];
  onNavigate: (id: string, o: DokumentOrdner) => void;
}) {
  // Bestimme den Typ der Übersicht
  const isStammdaten = ordner.pfad?.includes('/stammdaten/') || ordner.pfad === '/stammdaten';
  const isImmobilie = ordner.pfad?.includes('/immobilien/') && !ordner.pfad?.endsWith('/immobilien');
  const isMarke = ordner.pfad?.includes('/marken/');
  const isFirma = isStammdaten && !ordner.pfad?.endsWith('/stammdaten') && !ordner.pfad?.includes('/marken');
  const isBuchhaltung = ordner.pfad?.includes('/buchhaltung');

  // Pflichtdokumente für diesen Ordner
  const { pflichtdokumente, stats } = usePflichtdokumente(ordner.id);

  // Immer anzeigen wenn Unterordner vorhanden (nicht mehr nur bei Pflichtdokumenten)
  if (childOrdner.length === 0 && pflichtdokumente.length === 0 && !isBuchhaltung) return null;

  return (
    <div className="border-b border-gray-100">
      {/* Übersicht Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            {isImmobilie && <Home className="w-4 h-4 text-blue-500" />}
            {isFirma && <Building2 className="w-4 h-4 text-indigo-500" />}
            {isMarke && <Award className="w-4 h-4 text-purple-500" />}
            {isBuchhaltung && <Calculator className="w-4 h-4 text-emerald-500" />}
            {!isImmobilie && !isFirma && !isMarke && !isBuchhaltung && <FolderTree className="w-4 h-4 text-gray-500" />}
            <span className="text-sm font-semibold text-gray-800">{ordner.name}</span>
            {childOrdner.length > 0 && (
              <span className="text-xs text-gray-400 font-normal">{childOrdner.length} Ordner</span>
            )}
          </div>
          {stats.pflicht > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{stats.vorhanden}/{stats.pflicht}</span>
              {stats.fehlt > 0 ? (
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-5">{stats.fehlt} fehlt</Badge>
              ) : (
                <Badge className="text-[10px] px-1.5 py-0 h-5 bg-emerald-500 border-emerald-500">Vollständig</Badge>
              )}
            </div>
          )}
        </div>
        {stats.pflicht > 0 && <Progress value={stats.prozent} className="h-1.5 bg-gray-200" />}
      </div>

      {/* Unterordner als Kacheln */}
      {childOrdner.length > 0 && (
        <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {childOrdner.map(child => {
            const ChildIcon = getOrdnerIcon(child.icon);
            return (
              <button
                key={child.id}
                onClick={() => onNavigate(child.id, child)}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-gray-200 hover:border-teal-300 hover:bg-teal-50/30 transition-all text-left group"
              >
                <ChildIcon className="w-4 h-4 text-gray-400 group-hover:text-teal-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 truncate">{child.name}</p>
                  {child.dokument_count !== undefined && child.dokument_count > 0 && (
                    <p className="text-[10px] text-gray-400">{child.dokument_count} Dateien</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Pflichtdokumente Checkliste */}
      {pflichtdokumente.length > 0 && (
        <div className="divide-y divide-gray-50">
          {pflichtdokumente.map(pflicht => {
            const isFehlt = pflicht.status === 'fehlt';
            const isAbgelaufen = pflicht.status === 'abgelaufen';
            const isVorhanden = pflicht.status === 'vorhanden';

            return (
              <div
                key={pflicht.id}
                className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                  isFehlt ? 'hover:bg-amber-50/50' : isAbgelaufen ? 'bg-orange-50/30' : ''
                }`}
              >
                <div className="flex-shrink-0">
                  {isVorhanden && <CircleCheck className="w-5 h-5 text-emerald-500" />}
                  {isFehlt && (
                    <div className="relative">
                      <Circle className="w-5 h-5 text-gray-300" />
                      {pflicht.ist_pflicht && <AlertTriangle className="w-3 h-3 text-amber-500 absolute -top-1 -right-1" />}
                    </div>
                  )}
                  {isAbgelaufen && <AlertTriangle className="w-5 h-5 text-orange-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${
                    isFehlt ? 'text-gray-400' : isAbgelaufen ? 'text-orange-700' : 'text-foreground'
                  }`}>{pflicht.name}</p>
                  {pflicht.beschreibung && <p className="text-[11px] text-gray-400 truncate">{pflicht.beschreibung}</p>}
                </div>
                {pflicht.ist_pflicht && isFehlt && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-amber-300 text-amber-600">Pflicht</Badge>
                )}
                {!pflicht.ist_pflicht && isFehlt && <span className="text-[10px] text-gray-400">Optional</span>}
                {isAbgelaufen && pflicht.gueltig_bis && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-orange-300 text-orange-600">
                    Abgelaufen {formatDate(pflicht.gueltig_bis)}
                  </Badge>
                )}
                {isFehlt && <UploadCloud className="w-4 h-4 text-gray-300 flex-shrink-0" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// VORSCHAU PANEL — Inline PDF/Bild + Metadaten + Verknüpfungen + Buchung
// ============================================================

function VorschauPanel({
  dokument, onClose, onDelete, onDownload, onToggleFavorit, onRefresh, allOrdner,
}: {
  dokument: DokumentDatei; onClose: () => void; onDelete: () => void;
  onDownload: () => void; onToggleFavorit: () => void; onRefresh: () => void;
  allOrdner: DokumentOrdner[];
}) {
  const [activeTab, setActiveTab] = useState<'vorschau' | 'details' | 'verknuepfungen' | 'buchung'>('vorschau');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const { getPreviewUrl, updateDokumentTyp, updateBuchung, updateFreigabe, updateNotizen, updateDokumentTags, moveDokument, renameDokument: panelRename } = useDokumentAktionen();
  const { verknuepfungen, addVerknuepfung, removeVerknuepfung } = useVerknuepfungen(dokument.id);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(dokument.titel || dokument.dateiname.replace(/\.[^.]+$/, ''));

  useEffect(() => {
    setEditTitle(dokument.titel || dokument.dateiname.replace(/\.[^.]+$/, ''));
    setIsEditingTitle(false);
  }, [dokument.id]);

  const handleSaveTitle = async () => {
    const newTitle = editTitle.trim();
    if (newTitle && newTitle !== (dokument.titel || dokument.dateiname)) {
      try {
        await panelRename(dokument.id, newTitle);
        toast.success('Dokument umbenannt');
        onRefresh();
      } catch (e: any) {
        toast.error('Fehler: ' + e.message);
      }
    }
    setIsEditingTitle(false);
  };

  useEffect(() => {
    let cancelled = false;
    if (canPreviewInline(dokument.mime_type)) {
      setPreviewLoading(true);
      getPreviewUrl(dokument.dateipfad).then(url => {
        if (!cancelled) { setPreviewUrl(url); setPreviewLoading(false); }
      }).catch(() => { if (!cancelled) setPreviewLoading(false); });
    } else { setPreviewUrl(null); }
    return () => { cancelled = true; };
  }, [dokument.dateipfad, dokument.mime_type, getPreviewUrl]);

  const previewType = getPreviewType(dokument.mime_type);
  const FileIcon = getFileIcon(dokument.mime_type);
  const typLabel = DOKUMENT_TYPEN.find(t => t.value === dokument.dokument_typ)?.label || dokument.dokument_typ || 'Nicht erkannt';
  const buchungsLabel = BUCHUNGS_STATUS.find(s => s.value === dokument.buchungs_status);
  const freigabeLabel = FREIGABE_STATUS.find(s => s.value === dokument.freigabe_status);

  const findOrdnerName = (nodes: DokumentOrdner[], id: string): string => {
    for (const n of nodes) {
      if (n.id === id) return n.name;
      if (n.children) { const found = findOrdnerName(n.children, id); if (found) return found; }
    }
    return '';
  };
  const ordnerName = dokument.ordner_id ? findOrdnerName(allOrdner, dokument.ordner_id) : 'Eingangskorb';

  const tabs = [
    { id: 'vorschau' as const, label: 'Vorschau', icon: Eye },
    { id: 'details' as const, label: 'Details', icon: FileText },
    { id: 'verknuepfungen' as const, label: 'Verknüpfungen', icon: Link2 },
    { id: 'buchung' as const, label: 'Buchung', icon: Receipt },
  ];

  return (
    <div className="h-full flex flex-col bg-white border-l border-gray-200 shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 flex-shrink-0">
        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
          <FileIcon className="w-4.5 h-4.5 text-gray-500" />
        </div>
        <div className="flex-1 min-w-0">
          {isEditingTitle ? (
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveTitle();
                if (e.key === 'Escape') setIsEditingTitle(false);
              }}
              onBlur={handleSaveTitle}
              className="w-full px-2 py-1 text-sm font-semibold border border-teal-400 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500/30 bg-white"
              autoFocus
            />
          ) : (
            <p className="text-sm font-semibold truncate cursor-pointer hover:text-teal-700 group/title flex items-center gap-1" onClick={() => setIsEditingTitle(true)}>
              <span className="truncate">{dokument.titel || dokument.dateiname}</span>
              <Edit3 className="w-3 h-3 text-gray-300 opacity-0 group-hover/title:opacity-100 transition-opacity flex-shrink-0" />
            </p>
          )}
          <p className="text-[11px] text-gray-400 truncate">{ordnerName} · {formatFileSize(dokument.dateigroesse)}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={onToggleFavorit} className="p-1.5 rounded-lg hover:bg-gray-100" title="Favorit">
            <Star className={`w-4 h-4 ${dokument.ist_favorit ? 'fill-amber-400 text-amber-400' : 'text-gray-400'}`} />
          </button>
          <button onClick={onDownload} className="p-1.5 rounded-lg hover:bg-gray-100" title="Herunterladen">
            <Download className="w-4 h-4 text-gray-500" />
          </button>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 flex-shrink-0">
        {tabs.map(tab => {
          const TabIcon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium transition-colors border-b-2 ${
                activeTab === tab.id ? 'border-teal-500 text-teal-700 bg-teal-50/30' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}>
              <TabIcon className="w-3.5 h-3.5" />
              <span className="hidden xl:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'vorschau' && (
          <div className="h-full flex flex-col">
            {previewLoading ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
            ) : previewUrl && previewType === 'pdf' ? (
              <iframe src={`${previewUrl}#toolbar=1&navpanes=0`} className="flex-1 w-full border-0" title="PDF Vorschau" />
            ) : previewUrl && previewType === 'image' ? (
              <div className="flex-1 flex items-center justify-center p-4 bg-gray-50">
                <img src={previewUrl} alt={dokument.dateiname} className="max-w-full max-h-full object-contain rounded-lg shadow-sm" />
              </div>
            ) : previewUrl && previewType === 'text' ? (
              <iframe src={previewUrl} className="flex-1 w-full border-0 p-4 font-mono text-sm" title="Text Vorschau" />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <FileIcon className="w-16 h-16 text-gray-200 mb-4" />
                <p className="text-sm text-gray-500 mb-1">Vorschau nicht verfügbar</p>
                <p className="text-xs text-gray-400 mb-4">{dokument.mime_type || 'Unbekannter Dateityp'}</p>
                <button onClick={onDownload}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700">
                  <Download className="w-4 h-4" /> Herunterladen
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'details' && (
          <DetailsTab dokument={dokument} typLabel={typLabel} ordnerName={ordnerName}
            onUpdateTyp={async (typ) => { await updateDokumentTyp(dokument.id, typ); toast.success('Dokumenttyp aktualisiert'); onRefresh(); }}
            onUpdateNotizen={async (notizen) => { await updateNotizen(dokument.id, notizen); toast.success('Notizen gespeichert'); onRefresh(); }}
            onUpdateTags={async (tags) => { await updateDokumentTags(dokument.id, tags); toast.success('Labels aktualisiert'); onRefresh(); }}
            onMove={async (ordnerId) => { await moveDokument(dokument.id, ordnerId); toast.success('Dokument verschoben'); onRefresh(); }}
            onDelete={onDelete} allOrdner={allOrdner} />
        )}

        {activeTab === 'verknuepfungen' && (
          <VerknuepfungenTab verknuepfungen={verknuepfungen}
            onAdd={async (typ, id) => { await addVerknuepfung(typ, id); toast.success('Verknüpfung hinzugefügt'); }}
            onRemove={async (id) => { await removeVerknuepfung(id); toast.success('Verknüpfung entfernt'); }} />
        )}

        {activeTab === 'buchung' && (
          <BuchungTab dokument={dokument} buchungsLabel={buchungsLabel} freigabeLabel={freigabeLabel}
            onUpdateBuchung={async (updates) => { await updateBuchung(dokument.id, updates); toast.success('Buchung aktualisiert'); onRefresh(); }}
            onUpdateFreigabe={async (status) => { await updateFreigabe(dokument.id, status); toast.success('Freigabe aktualisiert'); onRefresh(); }} />
        )}
      </div>
    </div>
  );
}

// ============================================================
// DETAILS TAB
// ============================================================

function DetailsTab({
  dokument, typLabel, ordnerName, onUpdateTyp, onUpdateNotizen, onUpdateTags, onMove, onDelete, allOrdner,
}: {
  dokument: DokumentDatei; typLabel: string; ordnerName: string;
  onUpdateTyp: (typ: string) => Promise<void>;
  onUpdateNotizen: (notizen: string) => Promise<void>;
  onUpdateTags: (tags: string[]) => Promise<void>;
  onMove: (ordnerId: string) => Promise<void>;
  onDelete: () => void;
  allOrdner: DokumentOrdner[];
}) {
  const [editNotizen, setEditNotizen] = useState(false);
  const [notizen, setNotizen] = useState(dokument.notizen || '');
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showTypSelect, setShowTypSelect] = useState(false);
  const { tags: availableTags } = useTags();
  const activeTags = dokument.tags || [];

  const toggleTag = async (name: string) => {
    const next = activeTags.includes(name)
      ? activeTags.filter(t => t !== name)
      : [...activeTags, name];
    try {
      await onUpdateTags(next);
    } catch (e: any) {
      toast.error('Fehler: ' + e.message);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2.5">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Datei-Informationen</h4>
        <InfoRow label="Dateiname" value={dokument.dateiname} />
        <InfoRow label="Größe" value={formatFileSize(dokument.dateigroesse)} />
        <InfoRow label="Typ" value={dokument.mime_type || 'Unbekannt'} />
        <InfoRow label="Hochgeladen" value={formatDateTime(dokument.erstellt_am)} />
        {dokument.updated_at && <InfoRow label="Geändert" value={formatDateTime(dokument.updated_at)} />}
        <InfoRow label="Ordner" value={ordnerName} />
        <InfoRow label="Version" value={`v${dokument.version || 1}`} />
      </div>

      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Dokumenttyp</h4>
        {showTypSelect ? (
          <div className="space-y-1.5">
            {DOKUMENT_TYPEN.map(typ => (
              <button key={typ.value} onClick={() => { onUpdateTyp(typ.value); setShowTypSelect(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                  dokument.dokument_typ === typ.value ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'hover:bg-gray-50 text-gray-700'
                }`}>
                <span className="flex-1">{typ.label}</span>
                {dokument.dokument_typ === typ.value && <Check className="w-4 h-4 text-teal-600" />}
              </button>
            ))}
          </div>
        ) : (
          <button onClick={() => setShowTypSelect(true)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">
            <span className={dokument.dokument_typ ? 'text-foreground font-medium' : 'text-gray-400'}>{typLabel}</span>
            <Pencil className="w-3.5 h-3.5 text-gray-400" />
          </button>
        )}
      </div>

      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Labels</h4>
        {availableTags.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Keine Labels verfügbar</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {availableTags.map(tag => {
              const active = activeTags.includes(tag.name);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.name)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                    active ? 'text-white' : 'text-gray-600 bg-white hover:bg-gray-50'
                  }`}
                  style={
                    active
                      ? { backgroundColor: tag.farbe, borderColor: tag.farbe }
                      : { borderColor: tag.farbe, color: tag.farbe }
                  }
                >
                  {active && <Check className="w-3 h-3" />}
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Notizen</h4>
          {!editNotizen && (
            <button onClick={() => setEditNotizen(true)} className="p-1 rounded hover:bg-gray-100">
              <Pencil className="w-3 h-3 text-gray-400" />
            </button>
          )}
        </div>
        {editNotizen ? (
          <div className="space-y-2">
            <textarea value={notizen} onChange={(e) => setNotizen(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 resize-none"
              rows={4} placeholder="Notizen zum Dokument..." autoFocus />
            <div className="flex gap-2">
              <button onClick={() => setEditNotizen(false)} className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium hover:bg-gray-50">Abbrechen</button>
              <button onClick={() => { onUpdateNotizen(notizen); setEditNotizen(false); }}
                className="flex-1 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-xs font-medium hover:bg-teal-700">
                <Save className="w-3 h-3 inline mr-1" />Speichern
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{dokument.notizen || <span className="text-gray-400 italic">Keine Notizen</span>}</p>
        )}
      </div>

      <div className="space-y-2 pt-2 border-t border-gray-100">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Aktionen</h4>
        <button onClick={() => setShowMoveDialog(true)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">
          <FolderInput className="w-4 h-4 text-gray-400" /> Verschieben
        </button>
        <button onClick={onDelete}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-600 text-sm hover:bg-red-50">
          <Trash2 className="w-4 h-4" /> Löschen
        </button>
      </div>

      {showMoveDialog && (
        <MoveDialog allOrdner={allOrdner} currentOrdnerId={dokument.ordner_id}
          onMove={(id) => { onMove(id); setShowMoveDialog(false); }}
          onClose={() => setShowMoveDialog(false)} />
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | React.ReactNode }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-right max-w-[60%] truncate">{value}</span>
    </div>
  );
}

// ============================================================
// MOVE DIALOG
// ============================================================

function MoveDialog({ allOrdner, currentOrdnerId, onMove, onClose }: {
  allOrdner: DokumentOrdner[]; currentOrdnerId: string | null;
  onMove: (id: string) => void; onClose: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const flattenOrdner = (nodes: DokumentOrdner[], level: number = 0): { ordner: DokumentOrdner; level: number }[] => {
    const result: { ordner: DokumentOrdner; level: number }[] = [];
    for (const n of nodes) {
      result.push({ ordner: n, level });
      if (n.children) result.push(...flattenOrdner(n.children, level + 1));
    }
    return result;
  };

  const flat = flattenOrdner(allOrdner);
  const filtered = search ? flat.filter(f => f.ordner.name.toLowerCase().includes(search.toLowerCase())) : flat;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm max-h-[70vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-bold">Verschieben nach</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-3 py-2 border-b border-gray-100">
          <div className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-lg">
            <Search className="w-3.5 h-3.5 text-gray-400" />
            <input type="text" placeholder="Ordner suchen..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-xs focus:outline-none" autoFocus />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {filtered.map(({ ordner: o, level }) => {
            const Icon = getOrdnerIcon(o.icon);
            const isCurrent = o.id === currentOrdnerId;
            return (
              <button key={o.id} onClick={() => !isCurrent && setSelected(o.id)} disabled={isCurrent}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                  selected === o.id ? 'bg-teal-50 text-teal-700 border border-teal-200' :
                  isCurrent ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-50 text-gray-700'
                }`}
                style={{ paddingLeft: search ? '8px' : `${8 + level * 16}px` }}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{o.name}</span>
                {isCurrent && <span className="text-[10px] text-gray-400 ml-auto">(aktuell)</span>}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 p-3 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50">Abbrechen</button>
          <button onClick={() => selected && onMove(selected)} disabled={!selected}
            className="flex-1 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50">Verschieben</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// RENAME DIALOG (Dokument umbenennen)
// ============================================================

function RenameDokumentDialog({ dokument, onClose, onSubmit }: {
  dokument: DokumentDatei;
  onClose: () => void;
  onSubmit: (titel: string) => void | Promise<void>;
}) {
  const initialValue = dokument.titel || dokument.dateiname;
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-select den Basisnamen (ohne Endung) beim Öffnen
    const el = inputRef.current;
    if (!el) return;
    const lastDot = initialValue.lastIndexOf('.');
    const end = lastDot > 0 ? lastDot : initialValue.length;
    el.focus();
    el.setSelectionRange(0, end);
  }, [initialValue]);

  const canSubmit = value.trim().length > 0 && value.trim() !== initialValue && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try { await onSubmit(value.trim()); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-bold">Dokument umbenennen</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Titel</label>
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); }
                if (e.key === 'Escape') { e.preventDefault(); onClose(); }
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
              placeholder="Neuer Titel"
              disabled={saving}
            />
            <p className="mt-1.5 text-[11px] text-gray-400 truncate">Datei: {dokument.dateiname}</p>
          </div>
        </div>
        <div className="flex gap-2 p-3 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// VERKNÜPFUNGEN TAB
// ============================================================

function VerknuepfungenTab({ verknuepfungen, onAdd, onRemove }: {
  verknuepfungen: any[]; onAdd: (typ: string, id: string) => Promise<void>; onRemove: (id: string) => Promise<void>;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [newTyp, setNewTyp] = useState('akte');
  const [newId, setNewId] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!newId.trim()) return;
    setSaving(true);
    try {
      await onAdd(newTyp, newId.trim());
      setNewId(''); setShowAdd(false);
    } catch (e: any) { toast.error('Fehler: ' + e.message); }
    finally { setSaving(false); }
  };

  const getTypIcon = (typ: string) => {
    switch (typ) {
      case 'akte': return FileText;
      case 'kontakt': return Users;
      case 'aufgabe': return CheckCircle2;
      case 'fall': return FileSignature;
      case 'fahrzeug': return FileText;
      case 'immobilie': return Home;
      default: return Link2;
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Verknüpfungen</h4>
        <button onClick={() => setShowAdd(!showAdd)} className="p-1 rounded-lg hover:bg-gray-100">
          <Plus className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {showAdd && (
        <div className="p-3 bg-gray-50 rounded-lg space-y-2.5 border border-gray-200">
          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Typ</label>
            <select value={newTyp} onChange={(e) => setNewTyp(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20">
              {VERKNUEPFUNG_TYPEN.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">
              {newTyp === 'akte' ? 'Aktenzeichen' : newTyp === 'kontakt' ? 'Kontakt-Name/ID' : 'Referenz-ID'}
            </label>
            <input type="text" value={newId} onChange={(e) => setNewId(e.target.value)}
              placeholder={newTyp === 'akte' ? 'z.B. UG20260401-001' : 'ID oder Name eingeben...'}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()} />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowAdd(false)} className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium hover:bg-white">Abbrechen</button>
            <button onClick={handleAdd} disabled={!newId.trim() || saving}
              className="flex-1 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-xs font-medium hover:bg-teal-700 disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : 'Hinzufügen'}
            </button>
          </div>
        </div>
      )}

      {verknuepfungen.length === 0 ? (
        <div className="text-center py-8">
          <Link2 className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-xs text-gray-400">Keine Verknüpfungen</p>
          <button onClick={() => setShowAdd(true)} className="mt-2 text-xs text-teal-600 font-medium hover:text-teal-700">
            Verknüpfung hinzufügen
          </button>
        </div>
      ) : (
        <div className="space-y-1.5">
          {verknuepfungen.map(v => {
            const VIcon = getTypIcon(v.verknuepfung_typ);
            const typLabel = VERKNUEPFUNG_TYPEN.find(t => t.value === v.verknuepfung_typ)?.label || v.verknuepfung_typ;
            return (
              <div key={v.id} className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-lg group">
                <VIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{v.verknuepfung_id}</p>
                  <p className="text-[10px] text-gray-400">{typLabel}</p>
                </div>
                <button onClick={() => onRemove(v.id)}
                  className="p-1 rounded hover:bg-red-100 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-3 h-3 text-red-500" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// BUCHUNG TAB
// ============================================================

function BuchungTab({ dokument, buchungsLabel, freigabeLabel, onUpdateBuchung, onUpdateFreigabe }: {
  dokument: DokumentDatei; buchungsLabel: any; freigabeLabel: any;
  onUpdateBuchung: (updates: any) => Promise<void>;
  onUpdateFreigabe: (status: string) => Promise<void>;
}) {
  const [betrag, setBetrag] = useState(dokument.betrag?.toString() || '');
  const [buchungsDatum, setBuchungsDatum] = useState(dokument.buchungs_datum || '');
  const [saving, setSaving] = useState(false);

  const handleSaveBuchung = async () => {
    setSaving(true);
    try {
      await onUpdateBuchung({
        betrag: betrag ? parseFloat(betrag.replace(',', '.')) : null,
        buchungs_datum: buchungsDatum || null,
      });
    } catch (e: any) { toast.error('Fehler: ' + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Buchungsstatus</h4>
        <div className="grid grid-cols-2 gap-1.5">
          {BUCHUNGS_STATUS.map(s => (
            <button key={s.value} onClick={() => onUpdateBuchung({ buchungs_status: s.value })}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors border ${
                dokument.buchungs_status === s.value ? 'border-current bg-opacity-10' : 'border-gray-200 hover:bg-gray-50 text-gray-600'
              }`}
              style={dokument.buchungs_status === s.value ? { color: s.color, backgroundColor: s.color + '15', borderColor: s.color + '40' } : {}}>
              <CircleDot className="w-3 h-3" />{s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2.5">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Buchungsdaten</h4>
        <div>
          <label className="text-[11px] font-medium text-gray-500 mb-1 block">Betrag</label>
          <div className="flex items-center gap-2">
            <input type="text" value={betrag} onChange={(e) => setBetrag(e.target.value)}
              placeholder="0,00" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20" />
            <span className="text-sm text-gray-500 font-medium">{dokument.waehrung || 'EUR'}</span>
          </div>
        </div>
        <div>
          <label className="text-[11px] font-medium text-gray-500 mb-1 block">Buchungsdatum</label>
          <input type="date" value={buchungsDatum} onChange={(e) => setBuchungsDatum(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20" />
        </div>
        <button onClick={handleSaveBuchung} disabled={saving}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Speichern</>}
        </button>
      </div>

      <div className="space-y-2 pt-2 border-t border-gray-100">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Freigabe</h4>
        <div className="flex gap-1.5">
          {FREIGABE_STATUS.map(s => (
            <button key={s.value} onClick={() => onUpdateFreigabe(s.value)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium transition-colors border ${
                dokument.freigabe_status === s.value ? 'border-current bg-opacity-10' : 'border-gray-200 hover:bg-gray-50 text-gray-600'
              }`}
              style={dokument.freigabe_status === s.value ? { color: s.color, backgroundColor: s.color + '15', borderColor: s.color + '40' } : {}}>
              {s.value === 'freigegeben' ? <CheckCircle2 className="w-3 h-3" /> :
               s.value === 'abgelehnt' ? <XCircle className="w-3 h-3" /> :
               <Clock className="w-3 h-3" />}
              {s.label}
            </button>
          ))}
        </div>
        {dokument.freigabe_am && (
          <p className="text-[11px] text-gray-400">
            {dokument.freigabe_status === 'freigegeben' ? 'Freigegeben' : 'Abgelehnt'} am {formatDateTime(dokument.freigabe_am)}
          </p>
        )}
      </div>

      {(dokument.betrag || dokument.buchungs_status) && (
        <div className="p-3 bg-gray-50 rounded-lg space-y-1.5 border border-gray-200">
          <h4 className="text-xs font-semibold text-gray-500">Zusammenfassung</h4>
          {dokument.betrag && <InfoRow label="Betrag" value={formatCurrency(dokument.betrag, dokument.waehrung)} />}
          {buchungsLabel && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Status</span>
              <span className="font-medium" style={{ color: buchungsLabel.color }}>{buchungsLabel.label}</span>
            </div>
          )}
          {dokument.buchungs_datum && <InfoRow label="Datum" value={formatDate(dokument.buchungs_datum)} />}
        </div>
      )}
    </div>
  );
}

// ============================================================
// MOBILE VORSCHAU (Fullscreen Overlay)
// ============================================================

function MobileVorschau({
  dokument, onClose, onDelete, onDownload, onToggleFavorit, onRefresh, allOrdner,
}: {
  dokument: DokumentDatei; onClose: () => void; onDelete: () => void;
  onDownload: () => void; onToggleFavorit: () => void; onRefresh: () => void;
  allOrdner: DokumentOrdner[];
}) {
  const [activeTab, setActiveTab] = useState<'vorschau' | 'details' | 'verknuepfungen' | 'buchung'>('vorschau');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const { getPreviewUrl, updateDokumentTyp, updateBuchung, updateFreigabe, updateNotizen, updateDokumentTags, moveDokument, renameDokument: mobileRename } = useDokumentAktionen();
  const { verknuepfungen, addVerknuepfung, removeVerknuepfung } = useVerknuepfungen(dokument.id);
  const [isMobileEditingTitle, setIsMobileEditingTitle] = useState(false);
  const [mobileEditTitle, setMobileEditTitle] = useState(dokument.titel || dokument.dateiname.replace(/\.[^.]+$/, ''));

  useEffect(() => {
    setMobileEditTitle(dokument.titel || dokument.dateiname.replace(/\.[^.]+$/, ''));
    setIsMobileEditingTitle(false);
  }, [dokument.id]);

  const handleMobileSaveTitle = async () => {
    const newTitle = mobileEditTitle.trim();
    if (newTitle && newTitle !== (dokument.titel || dokument.dateiname)) {
      try {
        await mobileRename(dokument.id, newTitle);
        toast.success('Dokument umbenannt');
        onRefresh();
      } catch (e: any) {
        toast.error('Fehler: ' + e.message);
      }
    }
    setIsMobileEditingTitle(false);
  };

  useEffect(() => {
    let cancelled = false;
    if (canPreviewInline(dokument.mime_type)) {
      setPreviewLoading(true);
      getPreviewUrl(dokument.dateipfad).then(url => {
        if (!cancelled) { setPreviewUrl(url); setPreviewLoading(false); }
      }).catch(() => { if (!cancelled) setPreviewLoading(false); });
    }
    return () => { cancelled = true; };
  }, [dokument.dateipfad, dokument.mime_type, getPreviewUrl]);

  const previewType = getPreviewType(dokument.mime_type);
  const FileIcon = getFileIcon(dokument.mime_type);
  const typLabel = DOKUMENT_TYPEN.find(t => t.value === dokument.dokument_typ)?.label || dokument.dokument_typ || 'Nicht erkannt';
  const buchungsLabel = BUCHUNGS_STATUS.find(s => s.value === dokument.buchungs_status);
  const freigabeLabel = FREIGABE_STATUS.find(s => s.value === dokument.freigabe_status);

  const findOrdnerName = (nodes: DokumentOrdner[], id: string): string => {
    for (const n of nodes) {
      if (n.id === id) return n.name;
      if (n.children) { const found = findOrdnerName(n.children, id); if (found) return found; }
    }
    return '';
  };
  const ordnerName = dokument.ordner_id ? findOrdnerName(allOrdner, dokument.ordner_id) : 'Eingangskorb';

  const tabs = [
    { id: 'vorschau' as const, label: 'Vorschau', icon: Eye },
    { id: 'details' as const, label: 'Details', icon: FileText },
    { id: 'verknuepfungen' as const, label: 'Links', icon: Link2 },
    { id: 'buchung' as const, label: 'Buchung', icon: Receipt },
  ];

  return (
    <div className="fixed inset-0 bg-white z-[60] flex flex-col">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 flex-shrink-0 safe-area-top">
        <button onClick={onClose} className="p-2 -ml-2 rounded-lg hover:bg-gray-100">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          {isMobileEditingTitle ? (
            <input
              type="text"
              value={mobileEditTitle}
              onChange={(e) => setMobileEditTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleMobileSaveTitle();
                if (e.key === 'Escape') setIsMobileEditingTitle(false);
              }}
              onBlur={handleMobileSaveTitle}
              className="w-full px-2 py-1 text-sm font-semibold border border-teal-400 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500/30 bg-white"
              autoFocus
            />
          ) : (
            <p className="text-sm font-semibold truncate" onClick={() => setIsMobileEditingTitle(true)}>{dokument.titel || dokument.dateiname}</p>
          )}
          <p className="text-[11px] text-gray-400">{formatFileSize(dokument.dateigroesse)}</p>
        </div>
        <button onClick={onToggleFavorit} className="p-2 rounded-lg hover:bg-gray-100">
          <Star className={`w-5 h-5 ${dokument.ist_favorit ? 'fill-amber-400 text-amber-400' : 'text-gray-400'}`} />
        </button>
        <button onClick={onDownload} className="p-2 rounded-lg hover:bg-gray-100">
          <Download className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <div className="flex border-b border-gray-100 flex-shrink-0">
        {tabs.map(tab => {
          const TabIcon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium border-b-2 ${
                activeTab === tab.id ? 'border-teal-500 text-teal-700' : 'border-transparent text-gray-500'
              }`}>
              <TabIcon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto pb-24">
        {activeTab === 'vorschau' && (
          <div className="h-full flex flex-col">
            {previewLoading ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
            ) : previewUrl && previewType === 'pdf' ? (
              <iframe src={`${previewUrl}#toolbar=1`} className="flex-1 w-full border-0" title="PDF" />
            ) : previewUrl && previewType === 'image' ? (
              <div className="flex-1 flex items-center justify-center p-4 bg-gray-50">
                <img src={previewUrl} alt={dokument.dateiname} className="max-w-full max-h-full object-contain rounded-lg" />
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <FileIcon className="w-16 h-16 text-gray-200 mb-4" />
                <p className="text-sm text-gray-500 mb-4">Vorschau nicht verfügbar</p>
                <button onClick={onDownload} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium">
                  <Download className="w-4 h-4" /> Herunterladen
                </button>
              </div>
            )}
          </div>
        )}
        {activeTab === 'details' && (
          <DetailsTab dokument={dokument} typLabel={typLabel} ordnerName={ordnerName}
            onUpdateTyp={async (typ) => { await updateDokumentTyp(dokument.id, typ); toast.success('Dokumenttyp aktualisiert'); onRefresh(); }}
            onUpdateNotizen={async (notizen) => { await updateNotizen(dokument.id, notizen); toast.success('Notizen gespeichert'); onRefresh(); }}
            onUpdateTags={async (tags) => { await updateDokumentTags(dokument.id, tags); toast.success('Labels aktualisiert'); onRefresh(); }}
            onMove={async (ordnerId) => { await moveDokument(dokument.id, ordnerId); toast.success('Verschoben'); onRefresh(); }}
            onDelete={onDelete} allOrdner={allOrdner} />
        )}
        {activeTab === 'verknuepfungen' && (
          <VerknuepfungenTab verknuepfungen={verknuepfungen}
            onAdd={async (typ, id) => { await addVerknuepfung(typ, id); toast.success('Verknüpfung hinzugefügt'); }}
            onRemove={async (id) => { await removeVerknuepfung(id); toast.success('Entfernt'); }} />
        )}
        {activeTab === 'buchung' && (
          <BuchungTab dokument={dokument} buchungsLabel={buchungsLabel} freigabeLabel={freigabeLabel}
            onUpdateBuchung={async (u) => { await updateBuchung(dokument.id, u); toast.success('Aktualisiert'); onRefresh(); }}
            onUpdateFreigabe={async (s) => { await updateFreigabe(dokument.id, s); toast.success('Aktualisiert'); onRefresh(); }} />
        )}
      </div>
    </div>
  );
}

// ============================================================
// HAUPTKOMPONENTE
// ============================================================

export default function DokumentePage() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { ordner, loading: ordnerLoading, refetch: refetchOrdner, renameOrdner, deleteOrdner } = useOrdner();
  const [selectedOrdnerId, setSelectedOrdnerId] = useState<string | null>(null);
  const [selectedOrdner, setSelectedOrdner] = useState<DokumentOrdner | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showUpload, setShowUpload] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinUnlocked, setPinUnlocked] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSidebar, setShowMobileSidebar] = useState(true);
  const [selectedDokument, setSelectedDokument] = useState<DokumentDatei | null>(null);
  const [uploadForPflicht, setUploadForPflicht] = useState<PflichtDokument | null>(null);
  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
  const [showFilter, setShowFilter] = useState(false);
  const [dragOverOrdnerId, setDragOverOrdnerId] = useState<string | null>(null);
  const [draggingDokId, setDraggingDokId] = useState<string | null>(null);

  // Mobile Back-Handler: 3 Ebenen (Sidebar → Ordner-Inhalt → Dokument-Vorschau)
  // Ebene 1: Ordner-Inhalt → zurück zur Sidebar
  useMobileDetailBackHandler(
    isMobile && !showMobileSidebar && !selectedDokument,
    () => setShowMobileSidebar(true)
  );
  // Ebene 2: Dokument-Vorschau → zurück zum Ordner-Inhalt
  useMobileBackHandler(
    isMobile && !!selectedDokument,
    () => setSelectedDokument(null)
  );

  const { dokumente, loading: dokLoading, refetch: refetchDok } = useDokumente(selectedOrdnerId);
  const { search, results: searchResults, ordnerResults: searchOrdnerResults, loading: searchLoading, totalCount: searchTotalCount, clearResults: clearSearchResults } = useDokumentSuche();
  const { deleteDokument, toggleFavorit, getDownloadUrl, moveDokument, renameDokument } = useDokumentAktionen();

  // Move/Rename Dialog State (für Aktionen aus der Übersicht)
  const [moveTargetDok, setMoveTargetDok] = useState<DokumentDatei | null>(null);
  const [renameTargetDok, setRenameTargetDok] = useState<DokumentDatei | null>(null);
  const [renamingDokId, setRenamingDokId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Eingangskorb als Default
  useEffect(() => {
    if (!selectedOrdnerId && ordner.length > 0) {
      const eingangskorb = ordner.find(o => o.ist_system && o.icon === 'inbox');
      if (eingangskorb) { setSelectedOrdnerId(eingangskorb.id); setSelectedOrdner(eingangskorb); }
    }
  }, [ordner, selectedOrdnerId]);

  // Ordner standardmäßig zugeklappt lassen
  // Nutzer klappt bei Bedarf manuell auf

  // Suche mit Debounce
  useEffect(() => {
    if (!searchQuery.trim()) { clearSearchResults(); return; }
    const timer = setTimeout(() => { search(searchQuery); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, search, clearSearchResults]);

  const handleOrdnerSelect = useCallback((id: string, o: DokumentOrdner) => {
    if (o.ist_privat && !pinUnlocked) { setShowPinDialog(true); return; }
    setSelectedOrdnerId(id); setSelectedOrdner(o); setSearchQuery('');
    if (isMobile) setShowMobileSidebar(false);
  }, [pinUnlocked, isMobile]);

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);

  const handleDelete = useCallback(async (dok: DokumentDatei) => {
    if (!confirm(`"${dok.titel || dok.dateiname}" wirklich löschen?`)) return;
    try { await deleteDokument(dok.id); toast.success('Dokument gelöscht'); refetchDok(); setSelectedDokument(null); }
    catch (e: any) { toast.error('Fehler: ' + e.message); }
  }, [deleteDokument, refetchDok]);

  const handleRenameSubmit = useCallback(async (newTitel: string) => {
    if (!renameTargetDok) return;
    const trimmed = newTitel.trim();
    if (!trimmed || trimmed === (renameTargetDok.titel || renameTargetDok.dateiname)) {
      setRenameTargetDok(null);
      return;
    }
    try {
      await renameDokument(renameTargetDok.id, trimmed);
      toast.success('Dokument umbenannt');
      refetchDok();
      if (selectedDokument?.id === renameTargetDok.id) {
        setSelectedDokument({ ...selectedDokument, titel: trimmed });
      }
    } catch (e: any) {
      toast.error('Fehler: ' + e.message);
    } finally {
      setRenameTargetDok(null);
    }
  }, [renameTargetDok, renameDokument, refetchDok, selectedDokument]);

  const handleMoveSubmit = useCallback(async (targetOrdnerId: string) => {
    if (!moveTargetDok) return;
    try {
      await moveDokument(moveTargetDok.id, targetOrdnerId);
      toast.success('Dokument verschoben');
      refetchDok();
    } catch (e: any) {
      toast.error('Fehler: ' + e.message);
    } finally {
      setMoveTargetDok(null);
    }
  }, [moveTargetDok, moveDokument, refetchDok]);

  const handleStartRename = useCallback((dok: DokumentDatei, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setRenamingDokId(dok.id);
    setRenameValue(dok.titel || dok.dateiname.replace(/\.[^.]+$/, ''));
  }, []);

  const handleConfirmRename = useCallback(async (dok: DokumentDatei) => {
    const newTitle = renameValue.trim();
    if (!newTitle || newTitle === (dok.titel || dok.dateiname)) {
      setRenamingDokId(null);
      return;
    }
    try {
      await renameDokument(dok.id, newTitle);
      toast.success('Dokument umbenannt');
      refetchDok();
      if (selectedDokument?.id === dok.id) {
        setSelectedDokument({ ...dok, titel: newTitle });
      }
    } catch (e: any) {
      toast.error('Fehler beim Umbenennen: ' + e.message);
    }
    setRenamingDokId(null);
  }, [renameValue, renameDokument, refetchDok, selectedDokument]);

  const handleCancelRename = useCallback(() => {
    setRenamingDokId(null);
    setRenameValue('');
  }, []);

  const handleDownload = useCallback(async (dok: DokumentDatei) => {
    try {
      const url = await getDownloadUrl(dok.dateipfad);
      const a = document.createElement('a'); a.href = url; a.download = dok.dateiname; a.target = '_blank';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (e: any) { toast.error('Download fehlgeschlagen: ' + e.message); }
  }, [getDownloadUrl]);

  const handleToggleFavorit = useCallback(async (dok: DokumentDatei) => {
    try {
      await toggleFavorit(dok.id, dok.ist_favorit);
      toast.success(dok.ist_favorit ? 'Favorit entfernt' : 'Als Favorit markiert');
      refetchDok();
      if (selectedDokument?.id === dok.id) setSelectedDokument({ ...dok, ist_favorit: !dok.ist_favorit });
    } catch (e: any) { toast.error('Fehler: ' + e.message); }
  }, [toggleFavorit, refetchDok, selectedDokument]);

  const handleUploadForPflicht = useCallback((pflicht: PflichtDokument) => {
    setUploadForPflicht(pflicht);
    setShowUpload(true);
    toast(`Bitte laden Sie "${pflicht.name}" hoch`);
  }, []);

  const handleRefreshSelected = useCallback(() => {
    refetchDok();
    if (selectedDokument) {
      setTimeout(async () => {
        const { data } = await supabase.from('dokumente').select('*').eq('id', selectedDokument.id).single();
        if (data) setSelectedDokument(data as DokumentDatei);
      }, 300);
    }
  }, [refetchDok, selectedDokument]);

  // Drag & Drop Handler
  const handleDragStart = useCallback((e: React.DragEvent, dok: DokumentDatei) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ id: dok.id, name: dok.titel || dok.dateiname }));
    e.dataTransfer.effectAllowed = 'move';
    setDraggingDokId(dok.id);
  }, []);

  const handleOrdnerDragOver = useCallback((e: React.DragEvent, ordnerId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverOrdnerId(ordnerId);
  }, []);

  const handleOrdnerDragLeave = useCallback((e: React.DragEvent) => {
    setDragOverOrdnerId(null);
  }, []);

  const handleOrdnerDrop = useCallback(async (e: React.DragEvent, targetOrdnerId: string) => {
    e.preventDefault();
    setDragOverOrdnerId(null);
    setDraggingDokId(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.id) {
        await moveDokument(data.id, targetOrdnerId);
        toast.success(`"${data.name}" verschoben`);
        refetchDok();
      }
    } catch (err: any) {
      toast.error('Verschieben fehlgeschlagen: ' + (err.message || 'Unbekannter Fehler'));
    }
  }, [moveDokument, refetchDok]);

  // Ordner umbenennen
  const handleOrdnerRename = useCallback(async (id: string, currentName: string) => {
    const newName = prompt('Neuer Ordnername:', currentName);
    if (!newName || newName.trim() === currentName) return;
    try {
      await renameOrdner(id, newName.trim());
      toast.success(`Ordner umbenannt: "${newName.trim()}"`);
    } catch (e: any) {
      toast.error(e.message || 'Fehler beim Umbenennen');
    }
  }, [renameOrdner]);

  // Ordner löschen — ROBUSTER LÖSCHSCHUTZ mit doppelter Bestätigung
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ id: string; name: string } | null>(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleOrdnerDelete = useCallback(async (id: string, name: string) => {
    // Schritt 1: Erste Warnung
    if (!confirm(`⚠️ ACHTUNG: Ordner "${name}" wirklich löschen?\n\nDiese Aktion kann nicht rückgängig gemacht werden.\nSie müssen im nächsten Schritt den Ordnernamen exakt eingeben.`)) return;
    // Schritt 2: Sicherheitsdialog mit Namenseingabe öffnen
    setDeleteConfirmDialog({ id, name });
    setDeleteConfirmInput('');
  }, []);

  const executeOrdnerDelete = useCallback(async () => {
    if (!deleteConfirmDialog) return;
    setDeleteLoading(true);
    try {
      await deleteOrdner(deleteConfirmDialog.id, deleteConfirmInput);
      if (selectedOrdnerId === deleteConfirmDialog.id) {
        setSelectedOrdnerId(null);
        setSelectedOrdner(null);
      }
      toast.success(`Ordner "${deleteConfirmDialog.name}" gelöscht`);
      setDeleteConfirmDialog(null);
      setDeleteConfirmInput('');
    } catch (e: any) {
      toast.error(e.message || 'Fehler beim Löschen');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteOrdner, deleteConfirmDialog, deleteConfirmInput, selectedOrdnerId]);

  // Breadcrumb
  const breadcrumb = useMemo(() => {
    if (!selectedOrdner) return ['Dokumente'];
    const findPath = (nodes: DokumentOrdner[], targetId: string, path: string[]): string[] | null => {
      for (const n of nodes) {
        const currentPath = [...path, n.name];
        if (n.id === targetId) return currentPath;
        if (n.children) { const found = findPath(n.children, targetId, currentPath); if (found) return found; }
      }
      return null;
    };
    const found = findPath(ordner, selectedOrdner.id, []);
    return found ? ['Dokumente', ...found] : ['Dokumente'];
  }, [selectedOrdner, ordner]);

  // Unterordner des aktuellen Ordners
  const childOrdner = useMemo(() => {
    if (!selectedOrdner) return [];
    const findChildren = (nodes: DokumentOrdner[], id: string): DokumentOrdner[] => {
      for (const n of nodes) {
        if (n.id === id) return n.children || [];
        if (n.children) { const found = findChildren(n.children, id); if (found.length > 0) return found; }
      }
      return [];
    };
    return findChildren(ordner, selectedOrdner.id);
  }, [selectedOrdner, ordner]);

  // Gefilterte Dokumente
  const rawDokumente = searchQuery.trim() ? searchResults : dokumente;
  const displayDokumente = isFilterActive(filter) ? filterDokumente(rawDokumente, filter) : rawDokumente;
  const isLoading = searchQuery.trim() ? searchLoading : dokLoading;

  // Unterordner IMMER anzeigen wenn vorhanden (wie Google Drive / Dropbox)
  const showOrdnerUebersicht = useMemo(() => {
    if (!selectedOrdner || searchQuery) return false;
    return childOrdner.length > 0;
  }, [selectedOrdner, searchQuery, childOrdner]);

  // ============================================================
  // MOBILE LAYOUT
  // ============================================================

  if (isMobile) {
    return (
      <div className="flex flex-col h-full pb-24">
        {/* Header */}
        <div className="px-4 pt-3 pb-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            {!showMobileSidebar && (
              <button onClick={() => setShowMobileSidebar(true)} className="p-2 -ml-2 rounded-lg hover:bg-gray-100">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h1 className="text-xl font-bold text-foreground">
              {showMobileSidebar ? 'Dokumente' : (selectedOrdner?.name || 'Dokumente')}
            </h1>
            <div className="flex items-center gap-1">
              {!showMobileSidebar && (
                <button onClick={() => setShowFilter(!showFilter)}
                  className={`p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center ${
                    isFilterActive(filter) ? 'bg-teal-100 text-teal-700' : 'hover:bg-gray-100'
                  }`}>
                  <ListFilter className="w-5 h-5" />
                </button>
              )}
              <button onClick={() => setShowNewFolder(true)} className="p-2 rounded-lg hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
                <FolderPlus className="w-5 h-5 text-gray-500" />
              </button>
              <button onClick={() => setShowUpload(!showUpload)} className="p-2 rounded-lg bg-teal-600 text-white min-h-[44px] min-w-[44px] flex items-center justify-center">
                <Upload className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {showUpload && (
          <div className="px-4 pb-3 flex-shrink-0">
            <UploadZone ordnerId={selectedOrdnerId} onUploadComplete={() => { refetchDok(); setShowUpload(false); }} />
          </div>
        )}

        {/* Suche */}
        <div className="px-4 pb-2 flex-shrink-0">
          <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-200">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input type="text" placeholder="Dokumente durchsuchen..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm focus:outline-none placeholder-gray-400" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="p-1"><X className="w-3.5 h-3.5 text-gray-400" /></button>}
          </div>
        </div>

        {/* Mobile Filter */}
        {showFilter && !showMobileSidebar && (
          <div className="px-4 pb-2 flex-shrink-0">
            <FilterPanel filter={filter} onChange={setFilter} onReset={() => setFilter(DEFAULT_FILTER)}
              dokumentCount={rawDokumente.length} filteredCount={displayDokumente.length} />
          </div>
        )}

        {/* Content */}
        {showMobileSidebar && !searchQuery ? (
          <div className="flex-1 overflow-y-auto px-3">
            {ordnerLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
            ) : (
              <OrdnerTree ordner={ordner} selectedId={selectedOrdnerId} onSelect={handleOrdnerSelect}
                expandedIds={expandedIds} onToggleExpand={handleToggleExpand}
                onRename={handleOrdnerRename} onDelete={handleOrdnerDelete} />
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-3">
            {/* Mobile Breadcrumb */}
            {selectedOrdner && breadcrumb.length > 1 && (
              <div className="flex items-center gap-1 py-2 px-1 text-xs text-gray-500 overflow-x-auto flex-shrink-0 mb-1">
                {breadcrumb.map((part, i) => {
                  const isLast = i === breadcrumb.length - 1;
                  const isClickable = i > 0 && !isLast;
                  return (
                    <span key={i} className="flex items-center gap-1 flex-shrink-0">
                      {i > 0 && <ChevronRight className="w-3 h-3 text-gray-300" />}
                      {isClickable ? (
                        <button
                          onClick={() => {
                            // Finde den Ordner für diesen Breadcrumb-Teil
                            const findByName = (nodes: DokumentOrdner[], name: string, depth: number, targetDepth: number): DokumentOrdner | null => {
                              for (const n of nodes) {
                                if (depth === targetDepth && n.name === name) return n;
                                if (n.children) { const found = findByName(n.children, name, depth + 1, targetDepth); if (found) return found; }
                              }
                              return null;
                            };
                            const target = findByName(ordner, part, 1, i);
                            if (target) handleOrdnerSelect(target.id, target);
                          }}
                          className="text-teal-600 hover:text-teal-700 font-medium"
                        >
                          {part}
                        </button>
                      ) : (
                        <span className={isLast ? 'text-gray-700 font-medium' : 'text-gray-400'}>{part}</span>
                      )}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Unterordner-Kacheln */}
            {showOrdnerUebersicht && selectedOrdner && (
              <OrdnerUebersicht ordner={selectedOrdner} childOrdner={childOrdner} onNavigate={handleOrdnerSelect} />
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
            ) : searchQuery.trim() && (searchOrdnerResults.length > 0 || displayDokumente.length > 0) ? (
              <div>
                {/* Mobile Ordner-Suchergebnisse */}
                {searchOrdnerResults.length > 0 && (
                  <div className="px-1 py-2 mb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <FolderSearch className="w-3.5 h-3.5 text-teal-600" />
                      <span className="text-[11px] font-semibold text-gray-500 uppercase">Ordner ({searchOrdnerResults.length})</span>
                    </div>
                    <div className="space-y-1">
                      {searchOrdnerResults.slice(0, 5).map(o => (
                        <button key={o.id}
                          onClick={() => {
                            const findOrdnerById = (nodes: DokumentOrdner[], id: string): DokumentOrdner | null => {
                              for (const n of nodes) {
                                if (n.id === id) return n;
                                if (n.children) { const found = findOrdnerById(n.children, id); if (found) return found; }
                              }
                              return null;
                            };
                            const target = findOrdnerById(ordner, o.id);
                            if (target) { handleOrdnerSelect(target.id, target); setSearchQuery(''); }
                          }}
                          className="w-full flex items-center gap-3 p-3 bg-teal-50 rounded-lg text-left"
                        >
                          <FolderOpen className="w-5 h-5 text-teal-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-teal-800 truncate">{o.name}</p>
                            <p className="text-[10px] text-teal-600/70 truncate">{o.pfad}</p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-teal-400 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Mobile Dokument-Suchergebnisse */}
                {displayDokumente.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 px-1 mb-1">
                      <FileText className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-[11px] font-semibold text-gray-500 uppercase">Dokumente ({displayDokumente.length})</span>
                    </div>
                    {displayDokumente.map(dok => {
                      const FileIcon = getFileIcon(dok.mime_type);
                      const suchDok = dok as SuchErgebnis;
                      return (
                        <button key={dok.id} onClick={() => setSelectedDokument(dok)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 text-left">
                          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <FileIcon className="w-4.5 h-4.5 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <p className="text-sm font-medium truncate">{dok.titel || dok.dateiname}</p>
                              {hasStellungnahmeLabel(dok.tags) && <StellungnahmeBadge />}
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="text-[11px] text-gray-400">{formatFileSize(dok.dateigroesse)} · {formatDate(dok.erstellt_am)}</p>
                              {suchDok.ordner_name && (
                                <span className="text-[10px] text-teal-600 flex items-center gap-0.5">
                                  <MapPin className="w-2.5 h-2.5" />{suchDok.ordner_name}
                                </span>
                              )}
                            </div>
                          </div>
                          {dok.ist_favorit && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : displayDokumente.length === 0 && !showOrdnerUebersicht ? (
              <div className="text-center py-12">
                <FolderOpen className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">{searchQuery ? 'Keine Ergebnisse' : 'Keine Dokumente in diesem Ordner'}</p>
                {!searchQuery && <button onClick={() => setShowUpload(true)} className="mt-3 text-sm text-teal-600 font-medium">Datei hochladen</button>}
              </div>
            ) : displayDokumente.length > 0 ? (
              <div className="space-y-1">
                {displayDokumente.map(dok => {
                  const FileIcon = getFileIcon(dok.mime_type);
                  return (
                    <button key={dok.id} onClick={() => setSelectedDokument(dok)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 text-left">
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <FileIcon className="w-4.5 h-4.5 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="text-sm font-medium truncate">{dok.titel || dok.dateiname}</p>
                          {hasStellungnahmeLabel(dok.tags) && <StellungnahmeBadge />}
                        </div>
                        <p className="text-[11px] text-gray-400">{formatFileSize(dok.dateigroesse)} · {formatDate(dok.erstellt_am)}</p>
                      </div>
                      {dok.ist_favorit && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 flex-shrink-0" />}
                      {dok.buchungs_status && (
                        <span className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: BUCHUNGS_STATUS.find(s => s.value === dok.buchungs_status)?.color }} />
                      )}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        )}

        {/* Dialogs */}
        {showPinDialog && <PinDialog onClose={() => setShowPinDialog(false)} onSuccess={() => { setPinUnlocked(true); setShowPinDialog(false); }} />}
        {showNewFolder && <NeuerOrdnerDialog parentId={selectedOrdnerId} onClose={() => setShowNewFolder(false)} onCreated={refetchOrdner} />}
        {selectedDokument && (
          <MobileVorschau dokument={selectedDokument} onClose={() => setSelectedDokument(null)}
            onDelete={() => handleDelete(selectedDokument)} onDownload={() => handleDownload(selectedDokument)}
            onToggleFavorit={() => handleToggleFavorit(selectedDokument)} onRefresh={handleRefreshSelected}
            allOrdner={ordner} />
        )}
      </div>
    );
  }

  // ============================================================
  // DESKTOP LAYOUT
  // ============================================================

  return (
    <div className="p-2 sm:p-4 lg:p-6 h-[calc(100vh-3.5rem)] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dokumentenbereich</h1>
          <p className="text-sm text-gray-500 mt-0.5">Verwalten Sie alle Dokumente Ihrer Betriebe</p>
        </div>
      </div>

      {/* Main Content: 3-Spalten Layout */}
      <div className="flex gap-0 flex-1 min-h-0">
        {/* Linke Spalte: Ordnerstruktur */}
        <div className="w-64 flex-shrink-0 bg-white rounded-l-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Home className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ordner</span>
            </div>
            <button onClick={() => setShowNewFolder(true)} className="p-1.5 rounded-lg hover:bg-gray-100" title="Neuer Ordner">
              <FolderPlus className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {ordnerLoading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
            ) : (
              <OrdnerTree ordner={ordner} selectedId={selectedOrdnerId} onSelect={handleOrdnerSelect}
                expandedIds={expandedIds} onToggleExpand={handleToggleExpand}
                dragOverId={dragOverOrdnerId} onDragOver={handleOrdnerDragOver}
                onDragLeave={handleOrdnerDragLeave} onDrop={handleOrdnerDrop}
                onRename={handleOrdnerRename} onDelete={handleOrdnerDelete} />
            )}
          </div>
        </div>

        {/* Mitte: Dateiliste */}
        <div className={`flex-1 flex flex-col min-w-0 bg-white border-y border-gray-200 shadow-sm overflow-hidden ${
          selectedDokument ? '' : 'rounded-r-xl border-r'
        }`}>
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input type="text" placeholder="Dokumente durchsuchen..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent text-sm focus:outline-none placeholder-gray-400" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="p-1 hover:bg-gray-100 rounded"><X className="w-3.5 h-3.5 text-gray-400" /></button>}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setShowFilter(!showFilter)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  showFilter || isFilterActive(filter)
                    ? 'bg-teal-100 text-teal-700 border border-teal-200'
                    : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}>
                <ListFilter className="w-3.5 h-3.5" />
                Filter
                {isFilterActive(filter) && (
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                )}
              </button>
              <button onClick={() => setShowUpload(!showUpload)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  showUpload ? 'bg-teal-100 text-teal-700 border border-teal-200' : 'bg-teal-600 hover:bg-teal-700 text-white'
                }`}>
                <Upload className="w-3.5 h-3.5" /> Hochladen
              </button>
            </div>
          </div>

          {/* Breadcrumb — klickbar */}
          <div className="flex items-center gap-1.5 px-4 py-2 text-xs text-gray-500 border-b border-gray-100 flex-shrink-0 overflow-x-auto">
            {breadcrumb.map((part, i) => {
              const isLast = i === breadcrumb.length - 1;
              const isRoot = i === 0;
              return (
                <span key={i} className="flex items-center gap-1.5 flex-shrink-0">
                  {i > 0 && <ChevronRight className="w-3 h-3 text-gray-300" />}
                  {isLast ? (
                    <span className="text-gray-700 font-medium">{part}</span>
                  ) : isRoot ? (
                    <button
                      onClick={() => { setSelectedOrdnerId(null); setSelectedOrdner(null); setSearchQuery(''); }}
                      className="text-gray-500 hover:text-teal-600 hover:underline transition-colors cursor-pointer"
                    >
                      {part}
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        const findByName = (nodes: DokumentOrdner[], name: string, depth: number, targetDepth: number): DokumentOrdner | null => {
                          for (const n of nodes) {
                            if (depth === targetDepth && n.name === name) return n;
                            if (n.children) { const found = findByName(n.children, name, depth + 1, targetDepth); if (found) return found; }
                          }
                          return null;
                        };
                        const target = findByName(ordner, part, 1, i);
                        if (target) handleOrdnerSelect(target.id, target);
                      }}
                      className="text-gray-500 hover:text-teal-600 hover:underline transition-colors cursor-pointer"
                    >
                      {part}
                    </button>
                  )}
                </span>
              );
            })}
          </div>

          {/* Filter Panel */}
          {showFilter && (
            <FilterPanel filter={filter} onChange={setFilter} onReset={() => setFilter(DEFAULT_FILTER)}
              dokumentCount={rawDokumente.length} filteredCount={displayDokumente.length} />
          )}

          {/* Upload Zone */}
          {showUpload && (
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
              <UploadZone ordnerId={selectedOrdnerId} onUploadComplete={() => { refetchDok(); setShowUpload(false); }} />
            </div>
          )}

          {/* Dateiliste */}
          <div className="flex-1 overflow-y-auto">
            {/* Ordner-Übersicht für Firmen/Immobilien */}
            {showOrdnerUebersicht && selectedOrdner && (
              <OrdnerUebersicht ordner={selectedOrdner} childOrdner={childOrdner} onNavigate={handleOrdnerSelect} />
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
            ) : searchQuery.trim() && (searchOrdnerResults.length > 0 || displayDokumente.length > 0) ? (
              <div>
                {/* Ordner-Suchergebnisse */}
                {searchOrdnerResults.length > 0 && (
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <FolderSearch className="w-3.5 h-3.5 text-teal-600" />
                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Ordner ({searchOrdnerResults.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {searchOrdnerResults.map(o => (
                        <button key={o.id}
                          onClick={() => {
                            const findOrdnerById = (nodes: DokumentOrdner[], id: string): DokumentOrdner | null => {
                              for (const n of nodes) {
                                if (n.id === id) return n;
                                if (n.children) { const found = findOrdnerById(n.children, id); if (found) return found; }
                              }
                              return null;
                            };
                            const target = findOrdnerById(ordner, o.id);
                            if (target) { handleOrdnerSelect(target.id, target); setSearchQuery(''); }
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-teal-50 hover:bg-teal-100 border border-teal-200/60 rounded-lg transition-colors group"
                        >
                          <FolderOpen className="w-4 h-4 text-teal-600" />
                          <div className="text-left">
                            <p className="text-sm font-medium text-teal-800">{o.name}</p>
                            <p className="text-[10px] text-teal-600/70 truncate max-w-[200px]">{o.pfad}</p>
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dokument-Suchergebnisse mit Pfad-Info */}
                {displayDokumente.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-50">
                      <FileText className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Dokumente ({searchTotalCount > displayDokumente.length ? `${displayDokumente.length} von ${searchTotalCount}` : displayDokumente.length})</span>
                    </div>
                    {/* Tabellen-Header */}
                    <div className="flex items-center gap-3 px-4 py-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-50 sticky top-0 bg-white z-10">
                      <div className="w-8" />
                      <div className="flex-1">Name</div>
                      <div className="w-32">Ordner</div>
                      <div className="w-20 text-right">Größe</div>
                      <div className="w-24 text-right">Datum</div>
                      <div className="w-32" />
                    </div>
                    {displayDokumente.map(dok => {
                      const FileIcon = getFileIcon(dok.mime_type);
                      const isSelected = selectedDokument?.id === dok.id;
                      const suchDok = dok as SuchErgebnis;
                      return (
                        <div key={dok.id}
                          onClick={() => setSelectedDokument(dok)}
                          className={`flex items-center gap-3 px-4 py-2.5 transition-colors group border-b border-gray-50/80 cursor-pointer ${
                            isSelected ? 'bg-teal-50/60 border-l-2 border-l-teal-400' : 'hover:bg-gray-50'
                          }`}>
                          <div className="w-8 flex items-center justify-center">
                            <FileIcon className={`w-4 h-4 ${isSelected ? 'text-teal-600' : 'text-gray-400'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{dok.titel || dok.dateiname}</p>
                              {hasStellungnahmeLabel(dok.tags) && <StellungnahmeBadge />}
                            </div>
                            {suchDok.treffer_feld && suchDok.treffer_feld !== 'dateiname' && suchDok.treffer_feld !== 'titel' && (
                              <p className="text-[10px] text-amber-600 truncate mt-0.5">
                                <Sparkles className="w-2.5 h-2.5 inline mr-1" />
                                Treffer in: {suchDok.treffer_feld === 'beschreibung' ? 'Beschreibung' : suchDok.treffer_feld === 'notizen' ? 'Notizen' : suchDok.treffer_feld === 'dokument_typ' ? 'Dokumenttyp' : suchDok.treffer_feld}
                              </p>
                            )}
                          </div>
                          <div className="w-32">
                            {suchDok.ordner_name ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (dok.ordner_id) {
                                    const findOrdnerById = (nodes: DokumentOrdner[], id: string): DokumentOrdner | null => {
                                      for (const n of nodes) {
                                        if (n.id === id) return n;
                                        if (n.children) { const found = findOrdnerById(n.children, id); if (found) return found; }
                                      }
                                      return null;
                                    };
                                    const target = findOrdnerById(ordner, dok.ordner_id);
                                    if (target) { handleOrdnerSelect(target.id, target); setSearchQuery(''); }
                                  }
                                }}
                                className="flex items-center gap-1 text-[10px] text-teal-600 hover:text-teal-700 hover:underline truncate max-w-full"
                                title={`In Ordner anzeigen: ${suchDok.ordner_pfad || suchDok.ordner_name}`}
                              >
                                <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
                                <span className="truncate">{suchDok.ordner_name}</span>
                              </button>
                            ) : (
                              <span className="text-[10px] text-gray-400">Kein Ordner</span>
                            )}
                          </div>
                          <div className="w-20 text-right text-xs text-gray-500">{formatFileSize(dok.dateigroesse)}</div>
                          <div className="w-24 text-right text-xs text-gray-500">{formatDate(dok.erstellt_am)}</div>
                          <div className="w-32 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={(e) => { e.stopPropagation(); handleDownload(dok); }}
                              className="p-1.5 rounded-lg hover:bg-gray-200" title="Download">
                              <Download className="w-3.5 h-3.5 text-gray-500" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setRenameTargetDok(dok); }}
                              className="p-1.5 rounded-lg hover:bg-gray-200" title="Umbenennen">
                              <Pencil className="w-3.5 h-3.5 text-gray-500" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setMoveTargetDok(dok); }}
                              className="p-1.5 rounded-lg hover:bg-gray-200" title="Verschieben">
                              <FolderInput className="w-3.5 h-3.5 text-gray-500" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(dok); }}
                              className="p-1.5 rounded-lg hover:bg-red-100" title="Löschen">
                              <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            ) : displayDokumente.length === 0 && !showOrdnerUebersicht ? (
              <div className="text-center py-16">
                <FolderOpen className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <p className="text-sm text-gray-400 mb-1">{searchQuery ? 'Keine Ergebnisse' : isFilterActive(filter) ? 'Keine Dokumente für diesen Filter' : 'Keine Dokumente in diesem Ordner'}</p>
                {!searchQuery && !isFilterActive(filter) && <button onClick={() => setShowUpload(true)} className="text-sm text-teal-600 font-medium hover:text-teal-700">Datei hochladen</button>}
                {isFilterActive(filter) && <button onClick={() => setFilter(DEFAULT_FILTER)} className="text-sm text-teal-600 font-medium hover:text-teal-700">Filter zurücksetzen</button>}
              </div>
            ) : displayDokumente.length > 0 ? (
              <div>
                {/* Tabellen-Header */}
                <div className="flex items-center gap-3 px-4 py-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-50 sticky top-0 bg-white z-10">
                  <div className="w-5" />
                  <div className="w-8" />
                  <div className="flex-1">Name</div>
                  <div className="w-20 text-right">Typ</div>
                  <div className="w-20 text-right">Größe</div>
                  <div className="w-24 text-right">Datum</div>
                  <div className="w-36" />
                </div>

                {displayDokumente.map(dok => {
                  const FileIcon = getFileIcon(dok.mime_type);
                  const isSelected = selectedDokument?.id === dok.id;
                  const isDragging = draggingDokId === dok.id;
                  const typInfo = DOKUMENT_TYPEN.find(t => t.value === dok.dokument_typ);
                  return (
                    <div key={dok.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, dok)}
                      onDragEnd={() => setDraggingDokId(null)}
                      onClick={() => setSelectedDokument(dok)}
                      className={`flex items-center gap-3 px-4 py-2.5 transition-colors group border-b border-gray-50/80 cursor-pointer ${
                        isDragging ? 'opacity-40 bg-gray-100' :
                        isSelected ? 'bg-teal-50/60 border-l-2 border-l-teal-400' : 'hover:bg-gray-50'
                      }`}>
                      {/* Drag Handle */}
                      <div className="w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
                        <GripVertical className="w-3.5 h-3.5 text-gray-300" />
                      </div>
                      <div className="w-8 flex items-center justify-center">
                        <FileIcon className={`w-4 h-4 ${isSelected ? 'text-teal-600' : 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        {renamingDokId === dok.id ? (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleConfirmRename(dok);
                                if (e.key === 'Escape') handleCancelRename();
                              }}
                              onBlur={() => handleConfirmRename(dok)}
                              className="w-full px-2 py-1 text-sm font-medium border border-teal-400 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500/30 bg-white"
                              autoFocus
                            />
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate" onDoubleClick={(e) => handleStartRename(dok, e)}>{dok.titel || dok.dateiname}</p>
                              {hasStellungnahmeLabel(dok.tags) && <StellungnahmeBadge />}
                            </div>
                            {dok.titel && dok.titel !== dok.dateiname && (
                              <p className="text-[10px] text-gray-400 truncate">{dok.dateiname}</p>
                            )}
                          </>
                        )}
                      </div>
                      <div className="w-20 text-right">
                        {typInfo ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">{typInfo.label}</span>
                        ) : dok.buchungs_status ? (
                          <span className="w-2 h-2 rounded-full inline-block"
                            style={{ backgroundColor: BUCHUNGS_STATUS.find(s => s.value === dok.buchungs_status)?.color }} />
                        ) : null}
                      </div>
                      <div className="w-20 text-right text-xs text-gray-500">{formatFileSize(dok.dateigroesse)}</div>
                      <div className="w-24 text-right text-xs text-gray-500">{formatDate(dok.erstellt_am)}</div>
                      <div className="w-36 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); handleToggleFavorit(dok); }}
                          className="p-1.5 rounded-lg hover:bg-gray-200" title="Favorit">
                          <Star className={`w-3.5 h-3.5 ${dok.ist_favorit ? 'fill-amber-400 text-amber-400' : 'text-gray-400'}`} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDownload(dok); }}
                          className="p-1.5 rounded-lg hover:bg-gray-200" title="Download">
                          <Download className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setRenameTargetDok(dok); }}
                          className="p-1.5 rounded-lg hover:bg-gray-200" title="Umbenennen">
                          <Pencil className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setMoveTargetDok(dok); }}
                          className="p-1.5 rounded-lg hover:bg-gray-200" title="Verschieben">
                          <FolderInput className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(dok); }}
                          className="p-1.5 rounded-lg hover:bg-red-100" title="Löschen">
                          <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>

          {/* Status Bar */}
          <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/80 flex-shrink-0">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {displayDokumente.length} Datei{displayDokumente.length !== 1 ? 'en' : ''}
                {!searchQuery && selectedOrdner && ` in ${selectedOrdner.name}`}
                {isFilterActive(filter) && ` (gefiltert)`}
              </span>
              <div className="flex items-center gap-2">
                {isFilterActive(filter) && (
                  <button onClick={() => setFilter(DEFAULT_FILTER)} className="text-[10px] text-teal-600 font-medium hover:text-teal-700">
                    Filter zurücksetzen
                  </button>
                )}
                {searchQuery && (
                  <span className="text-[10px] text-gray-400">
                    Suchergebnisse{searchOrdnerResults.length > 0 ? ` • ${searchOrdnerResults.length} Ordner` : ''}
                    {searchTotalCount > displayDokumente.length ? ` • ${searchTotalCount} gesamt` : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Rechte Spalte: Vorschau Panel (Slide-In) */}
        {selectedDokument && (
          <div className="w-[420px] flex-shrink-0 rounded-r-xl overflow-hidden border-y border-r border-gray-200 shadow-sm animate-in slide-in-from-right duration-200">
            <VorschauPanel
              dokument={selectedDokument}
              onClose={() => setSelectedDokument(null)}
              onDelete={() => handleDelete(selectedDokument)}
              onDownload={() => handleDownload(selectedDokument)}
              onToggleFavorit={() => handleToggleFavorit(selectedDokument)}
              onRefresh={handleRefreshSelected}
              allOrdner={ordner}
            />
          </div>
        )}
      </div>

      {/* Dialogs */}
      {showPinDialog && <PinDialog onClose={() => setShowPinDialog(false)} onSuccess={() => { setPinUnlocked(true); setShowPinDialog(false); }} />}
      {showNewFolder && <NeuerOrdnerDialog parentId={selectedOrdnerId} onClose={() => setShowNewFolder(false)} onCreated={refetchOrdner} />}

      {/* Umbenennen-Dialog (aus Übersicht) */}
      {renameTargetDok && (
        <RenameDokumentDialog
          dokument={renameTargetDok}
          onClose={() => setRenameTargetDok(null)}
          onSubmit={handleRenameSubmit}
        />
      )}

      {/* Verschieben-Dialog (aus Übersicht) */}
      {moveTargetDok && (
        <MoveDialog
          allOrdner={ordner}
          currentOrdnerId={moveTargetDok.ordner_id}
          onMove={handleMoveSubmit}
          onClose={() => setMoveTargetDok(null)}
        />
      )}

      {/* Löschschutz-Bestätigungsdialog */}
      {deleteConfirmDialog && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl p-4 sm:p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-800">Ordner löschen</h3>
                <p className="text-xs text-gray-500">Diese Aktion kann nicht rückgängig gemacht werden</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700">
                Sie sind dabei, den Ordner <strong>"{deleteConfirmDialog.name}"</strong> dauerhaft zu löschen.
              </p>
              <p className="text-xs text-red-600 mt-2">
                Geben Sie den Ordnernamen exakt ein, um zu bestätigen:
              </p>
            </div>
            <input
              type="text"
              placeholder={`"${deleteConfirmDialog.name}" eingeben...`}
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && deleteConfirmInput === deleteConfirmDialog.name && executeOrdnerDelete()}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 text-sm"
              autoFocus
            />
            {deleteConfirmInput && deleteConfirmInput !== deleteConfirmDialog.name && (
              <p className="text-[11px] text-red-500 mb-3 -mt-2">Name stimmt nicht überein</p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => { setDeleteConfirmDialog(null); setDeleteConfirmInput(''); }}
                className="flex-1 px-3 py-2.5 min-h-[44px] rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={executeOrdnerDelete}
                disabled={deleteConfirmInput !== deleteConfirmDialog.name || deleteLoading}
                className="flex-1 px-3 py-2.5 min-h-[44px] rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Endgültig löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
